import { useState, useEffect } from 'react'
import { Search, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import DeviceCard from './DeviceCard'
import PendingDeviceCard from './PendingDeviceCard'
import DeviceDetailDrawer from './DeviceDetailDrawer'
import { equipmentOf, statusOf, compareDevices } from '../equipment'
import { ErrorState } from './ViewState'

export default function DevicesView() {
  const { t } = useTranslation()
  const { devices, pendingDevices, categories, mines, connected } = useWS()
  const { matchesScope } = useScope()
  const [query, setQuery] = useState('')
  const [type, setType] = useState('all')
  const [selected, setSelected] = useState(null)

  // Recalcula o status por tempo (Sem comunicação após COMMS_TIMEOUT_S) mesmo sem mensagem nova.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const q = query.toLowerCase()
  const scoped = devices.filter(matchesScope)
  const types = [...new Set(scoped.map(d => equipmentOf(d, categories, mines).label))].sort()

  // device provisionado mas ainda sem telemetria aparece como card "aguardando mensagens"
  // (pending), não como device, para não duplicar até a primeira comunicação.
  const waitingNames = new Set(pendingDevices.filter(p => p.status === 'provisioned').map(p => p.vmc_name))

  const filtered = scoped.filter(d => {
    if (waitingNames.has(d.name)) return false
    const e = equipmentOf(d, categories, mines)
    if (type !== 'all' && e.label !== type) return false
    return `${d.name} ${e.name} ${e.label}`.toLowerCase().includes(q)
  }).sort(compareDevices(d => { const e = equipmentOf(d, categories, mines); return e.name || d.name }))
  const filteredPending = pendingDevices.filter(p => p.vmc_name.toLowerCase().includes(q))

  // As paragens (uma chave por balde) somam num contador só: a pastilha diz quantas
  // máquinas estão paradas COM operador na cabine, e o detalhe do balde está no card.
  // O acumulador tem de tolerar chave nova, senão um estado que não esteja aqui vira
  // NaN na pastilha.
  const c = scoped.reduce((a, d) => {
    const k = statusOf(d).key
    const slot = k.startsWith('stop_') ? 'paused' : k
    a[slot] = (a[slot] || 0) + 1
    return a
  }, { op: 0, paused: 0, stop: 0, notelem: 0, down: 0 })
  const KPIS = [
    { label: t('common:device_status.operating'),    value: c.op,                  color: 'text-status-op' },
    { label: t('common:device_status.paused'),       value: c.paused,              color: 'text-status-nonprod' },
    { label: t('common:device_status.stopped'),      value: c.stop,                color: 'text-status-stop' },
    { label: t('common:device_status.no_telemetry'), value: c.notelem,             color: 'text-violet-600' },
    { label: t('common:device_status.no_comms'),     value: c.down,                color: 'text-status-down' },
    { label: t('ops:devices.pending'),               value: pendingDevices.length, color: 'text-slate-400 dark:text-slate-500' },
    { label: t('ops:devices.total'),                 value: scoped.length,         color: 'text-slate-700 dark:text-slate-200' },
  ]

  // mantém o detalhe sincronizado com a telemetria ao vivo
  const selectedDevice = selected ? devices.find(d => d.name === selected) || null : null

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-64 max-w-full">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('ops:devices.search_placeholder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
          <div className="relative">
            <select value={type} onChange={e => setType(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/30">
              <option value="all">{t('ops:devices.all_types')}</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none" />
          </div>
        </div>

        <div className="ml-auto flex gap-5 text-center">
          {KPIS.map(k => (
            <div key={k.label}>
              <div className={`text-xl font-bold leading-none ${k.color}`}>{k.value}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        {filteredPending.length === 0 && filtered.length === 0 ? (
          // Sem WebSocket a lista chega vazia: dizer "nenhum equipamento encontrado"
          // seria afirmar algo que a tela não sabe.
          !connected ? (
            <ErrorState message={t('ops:devices.ws_down')} />
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
              <div className="text-5xl mb-3">🛻</div>
              <p className="text-sm">{t('ops:devices.none_found')}</p>
              <p className="text-xs mt-1">{t('ops:devices.waiting_telemetry')}</p>
            </div>
          )
        ) : (
          <div className="grid items-start gap-5 grid-cols-1 md:grid-cols-2 2xl:grid-cols-3">
            {filteredPending.map(p => <PendingDeviceCard key={`pending-${p.id}`} pending={p} />)}
            {filtered.map(d => <DeviceCard key={d.name} device={d} onSelect={() => setSelected(d.name)} />)}
          </div>
        )}
      </div>

      <DeviceDetailDrawer device={selectedDevice} open={!!selectedDevice} onClose={() => setSelected(null)} />
    </div>
  )
}
